/*
 * The BetterFX page's profiles section (sections/betterfx-profiles.liquid).
 * Two computers, each with the BetterFX panel's General tab on screen and
 * its BetterFX search bar floating in front. On the studio desktop the
 * setup is saved with Save As ("Save profile as", a name is typed, Save),
 * then exported: the plugin writes <name>.betterfx.json to Downloads. The
 * file travels to the laptop, where Import opens the file picker ("Choose a
 * file"), the file is opened and the laptop takes on the profile: its name
 * in the Profile menu, the Assigned Shortcuts, the favorites' stars and the
 * search bar's accent and shortcut tag. Then it starts over.
 *
 * Plays only while on screen and while the tab is visible; leaving stops it
 * and it starts over next time. With reduced motion it shows the finished
 * result instead.
 */
(() => {
  const still = window.matchMedia('(prefers-reduced-motion: reduce)');
  const STOP = {};
  const DESIGN_W = 440;
  // Shortcuts are written the Mac way (⌥+⇧+B); on other systems the
  // modifiers read as Windows names them.
  const platform = (navigator.userAgentData && navigator.userAgentData.platform) || navigator.platform || navigator.userAgent;
  const isMac = /mac|iphone|ipad|ipod/i.test(platform);
  const parts = (keys) => String(keys).split('+').map((k) => k.trim()).filter(Boolean);
  // The panel's shortcut field (formatShortcut in the plugin):
  // "Option + Shift + B" on a Mac, "Alt + Shift + B" elsewhere.
  const PANEL = isMac
    ? { '⌥': 'Option', '⇧': 'Shift', '⌘': 'Command', '⌃': 'Control' }
    : { '⌥': 'Alt', '⇧': 'Shift', '⌘': 'Control', '⌃': 'Control' };
  const panelLabel = (keys) => parts(keys).map((k) => PANEL[k] || k).join(' + ');
  // The search bar's shortcut tag (formatShortcutTag in the desktop app):
  // symbols run together on a Mac, "Alt+Shift+B" on Windows.
  const TAG = { '⌥': 'Alt', '⇧': 'Shift', '⌘': 'Win', '⌃': 'Ctrl' };
  const tagLabel = (keys) => (isMac ? parts(keys).join('') : parts(keys).map((k) => TAG[k] || k).join('+'));
  // exportProfile()'s file name: the profile name, lowercased, dashed.
  const fileName = (name) =>
    (String(name)
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/^-+|-+$/g, '') || 'betterfx-profile') + '.betterfx.json';

  const init = (root) => {
    if (root.dataset.bpReady) return;
    root.dataset.bpReady = 'true';

    const q = (sel) => root.querySelector(sel);
    const stage = q('[data-bp-stage]');
    const card = q('[data-bp-file]');
    const elA = q('[data-bp-machine="a"]');
    const elB = q('[data-bp-machine="b"]');
    if (!stage || !card || !elA || !elB) return;

    const profileName = root.dataset.profileName || 'My setup';
    const accent = root.dataset.accent || 'purple';
    // the plugin's own profile names, in the page's language
    const defaultName = root.dataset.i18nDefault || 'Default';
    const defaultCopy = root.dataset.i18nDefaultCopy || 'Default copy';
    const caps = [...root.querySelectorAll('[data-bp-cap]')];
    const dots = [...root.querySelectorAll('.bp-dot')];
    const replay = q('[data-bp-replay]');

    root.querySelectorAll('[data-bp-label]').forEach((el) => (el.textContent = panelLabel(el.dataset.bpLabel)));
    root.querySelectorAll('[data-bp-tagkeys]').forEach((el) => (el.textContent = tagLabel(el.dataset.bpTagkeys)));
    root.querySelectorAll('[data-bp-filename]').forEach((el) => (el.textContent = fileName(profileName)));

    // Each computer is drawn at 440px wide and scaled to its column.
    const slots = [...root.querySelectorAll('[data-bp-slot]')];
    const fit = () =>
      slots.forEach((slot) => {
        const w = slot.clientWidth;
        if (w) slot.style.setProperty('--s', Math.min(1.12, w / DESIGN_W).toFixed(4));
      });
    fit();
    if ('ResizeObserver' in window) new ResizeObserver(fit).observe(stage);

    const machine = (el) => {
      const m = el.querySelector.bind(el);
      return {
        el,
        select: m('[data-bp-select]'),
        btn: (name) => m(`[data-bp-btn="${name}"]`),
        float: m('[data-bp-float]'),
        bar: m('[data-bp-bar]'),
        tag: m('[data-bp-tag]'),
        cursor: m('[data-bp-cursor]'),
        star: m('[data-bp-star]'),
        fields: [...el.querySelectorAll('.bp-field[data-bp-keys]')],
      };
    };
    const A = machine(elA);
    const B = machine(elB);
    const saveBox = elA.querySelector('[data-bp-save]');
    const saveBtn = saveBox && saveBox.querySelector('[data-bp-confirm]');
    const input = elA.querySelector('[data-bp-input]');
    const openBox = elB.querySelector('[data-bp-open]');
    const openBtn = openBox && openBox.querySelector('[data-bp-confirm]');
    const fileRow = elB.querySelector('[data-bp-frow]');
    if (!saveBox || !openBox || !input || !fileRow) return;

    let run = 0;
    let playing = false;
    let inView = false;

    const wait = (r, ms) =>
      new Promise((resolve, reject) => {
        window.setTimeout(() => (r === run ? resolve() : reject(STOP)), ms);
      });
    const restart = (el, cls) => {
      if (!el) return;
      el.classList.remove(cls);
      void el.offsetWidth;
      el.classList.add(cls);
    };

    // ---- the pointer, in the computer's own 440px drawing ----
    const spot = (m, el, fx = 0.5, fy = 0.5) => {
      const box = m.el.getBoundingClientRect();
      const s = box.width / DESIGN_W || 1;
      const b = el.getBoundingClientRect();
      return [(b.left - box.left + b.width * fx) / s, (b.top - box.top + b.height * fy) / s];
    };
    const place = (m, el, fx, fy) => {
      const [x, y] = spot(m, el, fx, fy);
      m.cursor.style.setProperty('--cx', `${x}px`);
      m.cursor.style.setProperty('--cy', `${y}px`);
    };
    const moveTo = (r, m, el, fx, fy, ms = 650) => {
      place(m, el, fx, fy);
      return wait(r, ms);
    };
    const jumpTo = (m, el, fx, fy) => {
      m.cursor.style.transition = 'none';
      place(m, el, fx, fy);
      void m.cursor.offsetWidth;
      m.cursor.style.transition = '';
    };
    const tap = (r, m) => {
      restart(m.cursor, 'is-click');
      return wait(r, 200);
    };

    // ---- the file card, in page px inside the stage ----
    const center = (el) => {
      const s = stage.getBoundingClientRect();
      const b = el.getBoundingClientRect();
      return [b.left - s.left + b.width / 2, b.top - s.top + b.height / 2];
    };
    // kept inside the stage, so on a phone it never pokes past the screen edge
    const cardAt = (x, y, scale = 1) => {
      const half = card.offsetWidth / 2;
      const cx = Math.max(half - 6, Math.min(stage.clientWidth - half + 6, x));
      return `translate(${(cx - half).toFixed(1)}px, ${(y - card.offsetHeight / 2).toFixed(1)}px) scale(${scale})`;
    };
    let cardPos = [0, 0];
    const stopCard = () => card.getAnimations().forEach((a) => a.cancel());
    const setCard = (x, y, scale, opacity) => {
      stopCard();
      cardPos = [x, y];
      card.style.transform = cardAt(x, y, scale);
      card.style.opacity = String(opacity);
    };
    // along a curve: up and over between side-by-side computers, a
    // sideways swing when they're stacked
    const flyCard = (r, to, ms, { endScale = 1, endOpacity = 1, lift = 1 } = {}) => {
      const [x0, y0] = cardPos;
      const [x1, y1] = to;
      const stacked = Math.abs(y1 - y0) > Math.abs(x1 - x0);
      const cx = (x0 + x1) / 2 + (stacked ? 70 * lift : 0);
      const cy = (y0 + y1) / 2 - (stacked ? 0 : 110 * lift);
      const frames = [];
      for (let i = 0; i <= 24; i += 1) {
        const u = i / 24;
        const t = u < 0.5 ? 4 * u * u * u : 1 - Math.pow(-2 * u + 2, 3) / 2;
        const x = (1 - t) * (1 - t) * x0 + 2 * (1 - t) * t * cx + t * t * x1;
        const y = (1 - t) * (1 - t) * y0 + 2 * (1 - t) * t * cy + t * t * y1;
        const sc = 1 + (endScale - 1) * t + (endScale === 1 ? Math.sin(Math.PI * t) * 0.06 : 0);
        frames.push({ transform: cardAt(x, y, sc), opacity: 1 + (endOpacity - 1) * t * t });
      }
      stopCard();
      card.animate(frames, { duration: ms, easing: 'linear', fill: 'forwards' });
      return wait(r, ms).then(() => setCard(x1, y1, endScale, endOpacity));
    };

    // ---- panel states ----
    const setProfile = (m, name, deletable) => {
      m.select.textContent = name;
      m.btn('delete').classList.toggle('is-off', !deletable);
    };
    const setLook = (m, look, withTag) => {
      m.bar.dataset.accent = look;
      m.tag.classList.toggle('is-off', !withTag);
    };
    const caption = (i) => {
      caps.forEach((c, n) => c.classList.toggle('is-on', n === i));
      dots.forEach((d, n) => d.classList.toggle('is-on', n === i));
    };
    const hover = (el, on) => el && el.classList.toggle('is-hover', on);

    const clear = () => {
      stage.classList.add('is-instant');
      [A, B].forEach((m) => {
        setProfile(m, defaultName, false);
        m.float.classList.remove('is-closed');
        m.cursor.classList.remove('is-on', 'is-click');
        m.el.querySelectorAll('.is-hover, .is-new, .is-pop, .is-fill').forEach((el) => el.classList.remove('is-hover', 'is-new', 'is-pop', 'is-fill'));
      });
      // the studio desktop: the editor's own setup, still in Default
      elA.classList.remove('is-empty');
      setLook(A, accent, true);
      saveBox.classList.remove('is-on');
      input.textContent = defaultCopy;
      input.classList.add('is-selected');
      // the laptop: a fresh install, Dark with the Black accent
      elB.classList.add('is-empty');
      setLook(B, 'black', false);
      openBox.classList.remove('is-on');
      fileRow.classList.remove('is-on', 'is-picked');
      setCard(0, 0, 0.6, 0);
      caption(0);
      void stage.offsetWidth;
      stage.classList.remove('is-instant');
    };

    const loop = async (r) => {
      // ---- 1. Save As on the studio desktop ----
      caption(0);
      await wait(r, 900);
      const saveAs = A.btn('saveas');
      jumpTo(A, saveAs, 0.6, 3.2);
      A.cursor.classList.add('is-on');
      await wait(r, 200);
      await moveTo(r, A, saveAs, 0.5, 0.6, 650);
      hover(saveAs, true);
      await tap(r, A);
      saveBox.classList.add('is-on');
      hover(saveAs, false);
      await wait(r, 750);
      input.classList.remove('is-selected');
      input.textContent = '';
      for (const ch of profileName) {
        input.textContent += ch;
        await wait(r, ch === ' ' ? 40 : 80);
      }
      await wait(r, 300);
      await moveTo(r, A, saveBtn, 0.5, 0.6, 600);
      hover(saveBtn, true);
      await tap(r, A);
      saveBox.classList.remove('is-on');
      hover(saveBtn, false);
      setProfile(A, profileName, true);
      restart(A.select, 'is-new');
      await wait(r, 1100);

      // ---- 2. Export ----
      caption(1);
      const exportBtn = A.btn('export');
      await moveTo(r, A, exportBtn, 0.5, 0.6, 650);
      hover(exportBtn, true);
      await tap(r, A);
      const [ex, ey] = center(exportBtn);
      setCard(ex, ey, 0.5, 0);
      await flyCard(r, [ex, ey - 46], 420, { endScale: 1, lift: 0.1 });
      hover(exportBtn, false);
      await wait(r, 450);
      A.cursor.classList.remove('is-on');

      // ---- 3. To the laptop ----
      const screenB = elB.querySelector('.bp-screen');
      const [sx, sy] = center(screenB);
      const sb = screenB.getBoundingClientRect();
      await flyCard(r, [sx, sy - sb.height * 0.35], 1350);
      caption(2);
      await wait(r, 250);

      // ---- 4. Import ----
      const importBtn = B.btn('import');
      jumpTo(B, importBtn, 0.4, 3.4);
      B.cursor.classList.add('is-on');
      await wait(r, 200);
      await moveTo(r, B, importBtn, 0.5, 0.6, 600);
      hover(importBtn, true);
      await tap(r, B);
      B.float.classList.add('is-closed');
      openBox.classList.add('is-on');
      hover(importBtn, false);
      await wait(r, 380);
      // the file lands in the picker
      await flyCard(r, center(fileRow), 520, { endScale: 0.45, endOpacity: 0, lift: 0.3 });
      fileRow.classList.add('is-on');
      await wait(r, 300);
      await moveTo(r, B, fileRow, 0.45, 0.6, 520);
      await tap(r, B);
      fileRow.classList.add('is-picked');
      await wait(r, 350);
      await moveTo(r, B, openBtn, 0.5, 0.6, 520);
      hover(openBtn, true);
      await tap(r, B);
      openBox.classList.remove('is-on');
      hover(openBtn, false);
      await wait(r, 260);

      // ---- 5. The laptop takes on the profile ----
      setProfile(B, profileName, true);
      restart(B.select, 'is-new');
      elB.classList.remove('is-empty');
      await wait(r, 260);
      restart(B.star, 'is-pop');
      B.fields.forEach((f) => restart(f, 'is-fill'));
      await wait(r, 900);
      B.cursor.classList.remove('is-on');
      // and the search bar opens in the profile's look
      setLook(B, accent, true);
      B.float.classList.remove('is-closed');
      caption(3);
      await wait(r, 4200);

      // ---- and again ----
      stage.style.opacity = '0.3';
      await wait(r, 400);
      clear();
      stage.style.opacity = '';
      await wait(r, 450);
    };

    const play = async () => {
      run += 1;
      const r = run;
      playing = true;
      clear();
      try {
        for (;;) await loop(r);
      } catch (error) {
        if (error !== STOP) throw error;
      }
    };

    const stop = () => {
      run += 1;
      playing = false;
      stage.style.opacity = '';
      clear();
    };

    // reduced motion: the profile is on both computers
    const finished = () => {
      [A, B].forEach((m) => {
        setProfile(m, profileName, true);
        setLook(m, accent, true);
        m.float.classList.remove('is-closed');
      });
      elB.classList.remove('is-empty');
      caption(3);
    };

    if (still.matches || !('IntersectionObserver' in window)) {
      finished();
      return;
    }

    clear();
    if (replay) {
      replay.hidden = false;
      replay.addEventListener('click', () => {
        if (!inView || document.hidden) return;
        stop();
        play();
      });
    }

    const update = () => {
      const go = inView && !document.hidden;
      if (go && !playing) play();
      else if (!go && playing) stop();
    };

    new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          inView = entry.isIntersecting;
        });
        update();
      },
      { threshold: 0.3 }
    ).observe(stage);
    document.addEventListener('visibilitychange', update);
  };

  const boot = () => document.querySelectorAll('[data-betterfx-profiles]').forEach(init);
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', boot);
  else boot();
  document.addEventListener('shopify:section:load', boot);
})();
